<template>
  <main class="pt-18 md:pt-[12.5vw] lg:pt-[5vw] bg-white">
    <section class="pb-14 lg:pb-28 pt-9 md:pt-18 bg-white">
      <div class="container-1 space-y-16 relative">
        <div class="lg:space-y-24">
          <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div class="lg:max-w-[41vw] space-y-1 lg:space-y-3" data-aos="fade-up" data-aos-easing="ease-out-sine" data-aos-duration="700" data-aos-delay="200">
              <div class="figtree font-semibold text-ip-color-3 text-xs lg:text-[.8vw] uppercase bg-ip-color-10 rounded-md w-fit p-[.38rem]">This is what we offer</div>
              <div class="figtree w-full font-semibold text-ip-color-3 text-2xl leading-[48px] md:text-[2.4vw] md:leading-[2.8vw] lg:text-[2.2vw] lg:leading-[2.6vw] ">ARC Framework</div>
              <div class="figtree font-medium w-full text-base leading-[24px] md:text-[1.4vw] md:leading-[2vw] lg:text-[1.1vw] lg:leading-[1.8vw]">Our framework is designed to enhance sustainability across three core areas: Act, Report, and Communicate. This framework helps you implement effective sustainability actions, produce detailed reports, and engage stakeholders through clear communication, driving continuous improvement and <br class="md:hidden">greater impact.</div>
            </div>
            <div class="py-14 lg:py-0 flex justify-center lg:static" data-aos="fade-up" data-aos-easing="ease-out-sine" data-aos-duration="700" data-aos-delay="200">
              <!-- <video Loop autoPlay playsInline muted preload="metadata" class="w-[100vw] h-auto max-w-lg xl:max-w-[35vw] user-select-none touch-none pointer-events-none">
                <source type="video/webm" src="/videos/arc.webm" />
                <source type="video/mp4" src="/videos/arc.mp4" />
              </video> -->
              <dotlottie-player src="https://lottie.host/108bf551-3e7f-44c4-a8d2-e2a62ac59dee/RrYOsNZclW.lottie" background="transparent" speed="1" class="arc-lottie w-[100vw] h-auto max-w-lg xl:max-w-[35vw]" Loop autoPlay></dotlottie-player>
            </div>
          </div>
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-14 lg:gap-20">
            <div v-for="(item, i) in arcDetail" :key="i" class="space-y-3" data-aos="fade-up" data-aos-easing="ease-out-sine" data-aos-duration="700" data-aos-delay="200">
              <div class="flex items-center gap-4">
                <figure class="max-w-8">
                  <VectorSingleArrow color="ip-color-10" />
                </figure>
                <div class="figtree text-lg md:text-[2vw] lg:text-[1.4vw] font-semibold uppercase text-ip-color-3">{{ item.title }}</div>
              </div>
              <div class="figtree font-medium text-base md:text-[1.4vw] lg:text-[1vw] md:leading-[2vw] lg:leading-[1.5vw]" v-html="item.desc"></div>
            </div>
          </div>
        </div>
        <div class="hidden md:block border-t border-[#CCCCCC] w-full"></div>
        <div class="w-full" data-aos="fade-up" data-aos-easing="ease-out-sine" data-aos-duration="700" data-aos-delay="200">
          <ClientOnly>
            <StackedStickyCard />
          </ClientOnly>
        </div>
      </div>
    </section>
    <Portfolio v-if="isMediumScreen" :items="items" @loadMore="loadMore" :noMore="noMore" />
    <PortfolioMobile v-else :items="items" @loadMore="loadMore" :noMore="noMore" />
  </main>
</template>

<script setup>
import { Interactions } from '../../interactions';
const interactionsVar = new Interactions();
const isMediumScreen = useMediaQuery('(min-width: 768px)');
const { $refreshAos } = useNuxtApp();
const scrollPos = ref(0);
const scrollDirection = ref(1);
const navbarStore = useNavbarStore();
const noMore = ref(false);
definePageMeta({
  layout: 'home-layout',
});

useSeoMeta({
  ogUrl: 'https://impacthink.id/our-services',
  title: 'Our Services - ImpacThink',
  ogTitle: 'Our Services - ImpacThink',
  twitterTitle: 'Our Services - ImpacThink',
  description: 'Our comprehensive approach helps you implement effective sustainability actions, produce detailed reports, and engage stakeholders through clear communication, driving continuous improvement and greater impact.',
  ogDescription: 'Our comprehensive approach helps you implement effective sustainability actions, produce detailed reports, and engage stakeholders through clear communication, driving continuous improvement and greater impact.',
  twitterDescription: 'Our comprehensive approach helps you implement effective sustainability actions, produce detailed reports, and engage stakeholders through clear communication, driving continuous improvement and greater impact.',
  ogType: 'website',
  ogImage: '/seo-image.webp',
  twitterImage: '/seo-image.webp',
  twitterCard: 'summary',
});

const arcDetail = ref([
  {
    title: 'ACT',
    desc: 'We enhance your ability to launch impactful <br class="hidden lg:inline">sustainability initiatives and build strategic <br class="hidden lg:inline">partnerships, driving substantial environmental <br class="hidden lg:inline">and social changes. This service empowers you <br class="hidden lg:inline">to act decisively and effect significant <br class="hidden lg:inline">transformation within your organization and <br class="hidden lg:inline">the broader community.',
  },

  {
    title: 'REPORT',
    desc: 'Our expertise supports you in crafting detailed <br class="hidden lg:inline">sustainability reports that showcase your <br class="hidden lg:inline">commitment and narrate your sustainability <br class="hidden lg:inline">journey creatively. By integrating stories, <br class="hidden lg:inline">visuals, and videos, we ensure your <br class="hidden lg:inline">achievements are comprehensible and <br class="hidden lg:inline">impactful for <br class="md:hidden">all stakeholders.',
  },

  {
    title: 'COMMUNICATE',
    desc: 'Our comprehensive stakeholder analysis and <br class="hidden lg:inline">ESG strategy development ensure your <br class="hidden lg:inline">sustainability efforts resonate with each <br class="hidden lg:inline">audience segment. We focus on strategic <br class="hidden lg:inline">engagement and clear communications to <br class="hidden lg:inline">ensure your sustainability vision is compelling <br class="hidden lg:inline">and leaves a lasting impact.',
  },
]);

const currentPage = ref(1);
const items = ref([]);
const { data: portfolioDoc } = await useAsyncData('portfolioDoc', () => $fetch('/api/cms/portfolio', {
  method: 'POST',
  body: { 
    action: 'get', 
    skip: 0,
    perPage: 5,
    status: 'active'
  }
}), {
  transform: (_portfolioDoc) => _portfolioDoc.result,
});
items.value = portfolioDoc.value?.docs || [];
currentPage.value = portfolioDoc.value?.currentPage || 1;

const startLenis = () => {
  if (interactionsVar) {
    interactionsVar.createSmoothScroll();
    interactionsVar.startLenis();
    interactionsVar.lenis.on('scroll', (e) => {
      scrollPos.value = e.scroll;
      scrollDirection.value = e.direction;
      // lenisProgress.value.style.width = (e.progress * 100) + '%';
      if (scrollDirection.value < 0) {
        navbarStore.showNavbarValue();
      } else {
        navbarStore.hideNavbarValue();
      }
    });
  }
};

const destroyLenis = () => {
  if (interactionsVar) interactionsVar.destroyLenis();
};

onMounted(() => {
  nextTick(() => {
    $refreshAos();
    startLenis();
  });
});

const loadMore = async () => {
  const skip = items.value.length;
  const { data: portfolioDoc } = await useAsyncData(`portfolioDoc-${currentPage.value + 1}`, () => $fetch('/api/cms/portfolio', {
    method: 'POST',
    body: { 
      action: 'load-more', 
      skip,
      perPage: 5,
      status: 'active'
    }
  }), {
    transform: (_portfolioDoc) => _portfolioDoc.result,
  });

  if (portfolioDoc.value?.docs?.length) {
    items.value.push(...portfolioDoc.value.docs);
    currentPage.value++;
  } else {
    noMore.value = true;
  }
};
</script>

<style lang="scss" scoped>

</style>
